import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { CreatePartnerDtoPartnerTypeEnum } from "../../../api";

export type PartnerType = CreatePartnerDtoPartnerTypeEnum;

export type CountryCode = "QA" | "SA" | "KW" | "BH";

export type SignupMethod = "email" | "phone";

export interface RegisterWizardState {
  signupMethod: SignupMethod;

  // Credentials
  email: string;
  phoneNumber: string;
  fullName: string;
  /** Stored in memory only via persist; wiped on reset. */
  password: string;

  // Partner type
  partnerType: PartnerType | "";

  // Business details
  businessName: string;
  crNumber: string;
  taxNumber: string;
  countryCode: CountryCode | "";

  /** True once otpRegister has succeeded and an OTP has been sent. */
  registered: boolean;

  /**
   * Saves the credentials step. When the identity changes (other sign-up
   * method, or another email/phone), everything entered for the previous one —
   * partner type, business details, registration — is discarded first, so an
   * email sign-up never shows data from an earlier phone attempt.
   */
  startSignup: (
    credentials:
      | { signupMethod: "email"; email: string; fullName: string; password: string }
      | { signupMethod: "phone"; phoneNumber: string; fullName: string },
  ) => void;
  setPartnerType: (type: PartnerType) => void;
  setBusinessDetails: (fields: {
    businessName: string;
    crNumber: string;
    taxNumber: string;
    countryCode: CountryCode;
  }) => void;
  setRegistered: (registered: boolean) => void;
  reset: () => void;
}

const INITIAL: Omit<
  RegisterWizardState,
  | "startSignup"
  | "setPartnerType"
  | "setBusinessDetails"
  | "setRegistered"
  | "reset"
> = {
  signupMethod: "email",
  email: "",
  phoneNumber: "",
  fullName: "",
  password: "",
  partnerType: "",
  businessName: "",
  crNumber: "",
  taxNumber: "",
  countryCode: "",
  registered: false,
};

export const useRegisterWizard = create<RegisterWizardState>()(
  persist(
    (set) => ({
      ...INITIAL,
      startSignup: (credentials) =>
        set((state) => {
          const sameIdentity =
            credentials.signupMethod === state.signupMethod &&
            (credentials.signupMethod === "email"
              ? credentials.email === state.email
              : credentials.phoneNumber === state.phoneNumber);
          return sameIdentity
            ? credentials
            : { ...INITIAL, ...credentials };
        }),
      setPartnerType: (partnerType) => set({ partnerType }),
      setBusinessDetails: (fields) => set(fields),
      setRegistered: (registered) => set({ registered }),
      reset: () => set({ ...INITIAL }),
    }),
    {
      name: "register-wizard",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
